import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLocale } from '@/contexts/LocaleContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Sparkles, Loader2, Download, Image, FolderOpen, Clock, X, Shirt, TrendingUp, Search, Wand2, Zap, Crown, ShoppingCart, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import ProPaywall from '@/components/ProPaywall';
import FashionDesigner from '@/components/FashionDesigner';
import TexTrendAI from '@/components/TexTrendAI';
import VisualSearch from '@/components/VisualSearch';
import PhotoOptimizer from '@/components/PhotoOptimizer';
import TexChatFloating from '@/components/TexChatFloating';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fetch with auth token from localStorage
const fetchWithAuth = async (url, options = {}) => {
  const token = localStorage.getItem('texhub_token') || sessionStorage.getItem('texhub_token');
  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
      ...options.headers
    }
  });
};

const getGarmentOptions = (isUSA) => [
  { id: 'camisa', name: isUSA ? 'Dress Shirt' : 'Camisa', icon: '👔' },
  { id: 'playera', name: isUSA ? 'T-Shirt' : 'Playera / T-Shirt', icon: '👕' },
  { id: 'blusa', name: isUSA ? 'Blouse' : 'Blusa', icon: '👚' },
  { id: 'pantalon', name: isUSA ? 'Pants' : 'Pantalón', icon: '👖' },
  { id: 'vestido', name: isUSA ? 'Dress' : 'Vestido', icon: '👗' },
  { id: 'blazer', name: isUSA ? 'Blazer / Jacket' : 'Blazer / Saco', icon: '🧥' },
  { id: 'falda', name: isUSA ? 'Skirt' : 'Falda', icon: '🩱' },
  { id: 'chamarra', name: isUSA ? 'Jacket' : 'Chamarra / Jacket', icon: '🧥' },
  { id: 'pijama', name: isUSA ? 'Pajamas' : 'Pijama', icon: '🛏️' },
  { id: 'traje_bano', name: isUSA ? 'Swimwear' : 'Traje de baño', icon: '🩱' },
];

const TexVisionPage = () => {
  const { user } = useAuth();
  const { isUSA } = useLocale();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('designer'); // 'designer', 'visual-search', 'photo-optimizer', 'trends', or 'gallery'
  const [credits, setCredits] = useState({ 
    monthly_credits: 100, 
    purchased_credits: 0, 
    total_available: 100, 
    credits_used: 0, 
    remaining: 100, 
    is_unlimited: false 
  });
  const [creditCosts, setCreditCosts] = useState({});
  const [loadingUsage, setLoadingUsage] = useState(true);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedHistoryImage, setSelectedHistoryImage] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const [showNoCreditsModal, setShowNoCreditsModal] = useState(false);
  const [neededCredits, setNeededCredits] = useState(0);
  
  // Credit purchase modal
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [creditPackages, setCreditPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [purchasingPackage, setPurchasingPackage] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);
  
  // Image to send to optimizer from other tools
  const [optimizerImage, setOptimizerImage] = useState(null);
  
  // Image to send to designer from other tools
  const [designerImage, setDesignerImage] = useState(null);
  
  // Image to send to visual search from other tools
  const [visualSearchImage, setVisualSearchImage] = useState(null);
  
  // TexChat Floating state
  const [showTexChat, setShowTexChat] = useState(false);
  const [texChatContext, setTexChatContext] = useState({ type: null, data: null, image: null });
  
  // Get translated options (used in gallery)
  const GARMENT_OPTIONS = getGarmentOptions(isUSA);

  const fetchHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/api/texvision/history?limit=50`);
      if (res.ok) {
        const data = await res.json();
        setHistory(data);
      }
    } catch (err) {
      console.error('Error fetching history:', err);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  const fetchUsage = useCallback(async () => {
    setLoadingUsage(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/api/texvision/usage`);
      if (res.ok) {
        const data = await res.json();
        if (data.credits) {
          setCredits(data.credits);
        }
        if (data.costs) {
          setCreditCosts(data.costs);
        }
      }
    } catch (err) {
      console.error('Error fetching usage:', err);
    } finally {
      setLoadingUsage(false);
    }
  }, []);

  // Fetch credit packages
  const fetchCreditPackages = useCallback(async () => {
    setLoadingPackages(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/api/texvision/credit-packages`);
      if (res.ok) {
        const data = await res.json();
        setCreditPackages(data.packages || []);
      }
    } catch (err) {
      console.error('Error fetching packages:', err);
    } finally {
      setLoadingPackages(false);
    }
  }, []);

  // Poll payment status after returning from Stripe
  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      setCheckingPayment(false);
      toast.error(isUSA ? 'Payment verification timed out. Please check your email.' : 'Tiempo de verificación agotado. Revisa tu correo.');
      return;
    }

    try {
      const res = await fetchWithAuth(`${API_URL}/api/texvision/checkout-status/${sessionId}`);
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to check payment status');
      }
      
      if (data.payment_status === 'paid') {
        setCheckingPayment(false);
        toast.success(isUSA ? `Payment successful! ${data.credits_added} credits added.` : `¡Pago exitoso! ${data.credits_added} créditos añadidos.`);
        fetchUsage(); // Refresh credits
        // Clear URL params
        setSearchParams({});
        return;
      } else if (data.status === 'expired') {
        setCheckingPayment(false);
        toast.error(isUSA ? 'Payment session expired.' : 'Sesión de pago expirada.');
        setSearchParams({});
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        setCheckingPayment(false);
        toast.error(isUSA ? 'Error verifying payment.' : 'Error verificando pago.');
      }
    }
  }, [isUSA, fetchUsage, setSearchParams]);

  // Check for Stripe return on mount
  useEffect(() => {
    const payment = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    
    if (payment === 'success' && sessionId) {
      setCheckingPayment(true);
      toast.info(isUSA ? 'Verifying payment...' : 'Verificando pago...');
      pollPaymentStatus(sessionId);
    } else if (payment === 'cancelled') {
      toast.info(isUSA ? 'Payment cancelled.' : 'Pago cancelado.');
      setSearchParams({});
    }
  }, [searchParams, pollPaymentStatus, isUSA, setSearchParams]);

  // Purchase credits - creates Stripe checkout session
  const purchaseCredits = async (packageId) => {
    setPurchasingPackage(packageId);
    try {
      const originUrl = window.location.origin;
      const res = await fetchWithAuth(`${API_URL}/api/texvision/create-checkout-session`, {
        method: 'POST',
        body: JSON.stringify({ 
          package_id: packageId,
          origin_url: originUrl
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        // Redirect to Stripe Checkout
        if (data.url) {
          window.location.href = data.url;
        } else {
          throw new Error('No checkout URL received');
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || (isUSA ? 'Failed to create checkout session' : 'Error al crear sesión de pago'));
      }
    } catch (err) {
      console.error('Error creating checkout session:', err);
      toast.error(isUSA ? 'Error processing purchase' : 'Error al procesar la compra');
    } finally {
      setPurchasingPackage(null);
    }
  };

  // Open credit modal
  const openCreditModal = () => {
    setShowCreditModal(true);
    fetchCreditPackages();
  };

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  // Show no credits modal when user runs out
  useEffect(() => {
    if (!loadingUsage && credits.remaining === 0 && credits.total_available > 0) {
      // User had credits but ran out - show upgrade modal
      setShowNoCreditsModal(true);
    } else if (!loadingUsage && credits.remaining === 0 && credits.total_available === 0) {
      // Free user with no credits at all
      const userPlan = user?.subscription_plan || 'free';
      if (userPlan === 'free') {
        setShowPaywall(true);
      }
    }
  }, [loadingUsage, credits, user]);

  // Function to check credits before action and show modal if needed
  const checkCreditsBeforeAction = (requiredCredits) => {
    if (credits.remaining < requiredCredits) {
      setNeededCredits(requiredCredits);
      setShowNoCreditsModal(true);
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (activeTab === 'gallery') {
      fetchHistory();
    }
  }, [activeTab, fetchHistory]);

  // Listen for switch-to-designer event from TEXTREND AI
  useEffect(() => {
    const handleSwitchToDesigner = () => {
      setActiveTab('designer');
    };
    
    window.addEventListener('switch-to-designer', handleSwitchToDesigner);
    return () => window.removeEventListener('switch-to-designer', handleSwitchToDesigner);
  }, []);

  // Handle sending image to Photo Optimizer from other tools
  const handleSendToOptimizer = useCallback((imageBase64) => {
    setOptimizerImage(imageBase64);
    setActiveTab('photo-optimizer');
  }, []);

  // Handle credit-related errors from child components
  const handleCreditError = useCallback((errorMessage, requiredCredits = 0) => {
    // Check if error is credit-related
    if (errorMessage?.toLowerCase().includes('crédito') || 
        errorMessage?.toLowerCase().includes('credit') ||
        errorMessage?.toLowerCase().includes('quedaste sin')) {
      setNeededCredits(requiredCredits);
      setShowNoCreditsModal(true);
      return true; // Error was handled
    }
    return false; // Error was not credit-related
  }, []);

  // Handle sending image to Fashion Designer from other tools (as pattern/garment reference)
  const handleSendToDesigner = useCallback((imageBase64) => {
    // Store in sessionStorage so FashionDesigner can pick it up on mount
    sessionStorage.setItem('optimizer_to_designer', JSON.stringify({
      image_base64: imageBase64,
      type: 'pattern',
      timestamp: Date.now()
    }));
    setActiveTab('designer');
  }, []);

  // Handle sending image to Fashion Designer as fabric texture
  const handleSendToDesignerAsFabric = useCallback((imageBase64) => {
    // Store in sessionStorage so FashionDesigner can pick it up on mount
    sessionStorage.setItem('optimizer_to_designer', JSON.stringify({
      image_base64: imageBase64,
      type: 'fabric',
      timestamp: Date.now()
    }));
    setActiveTab('designer');
  }, []);

  // Handle sending image to Visual Search from other tools
  const handleSendToVisualSearch = useCallback((imageBase64) => {
    // Store in sessionStorage so VisualSearch can pick it up on mount
    sessionStorage.setItem('optimizer_to_search', JSON.stringify({
      image_base64: imageBase64,
      timestamp: Date.now()
    }));
    setActiveTab('visual-search');
  }, []);

  const handleDownload = async (imageData, index = 0) => {
    if (!imageData) {
      toast.error(isUSA ? 'No image to download' : 'No hay imagen para descargar');
      return;
    }
    
    const filename = `texvision_design_${index + 1}_${Date.now()}.png`;
    
    // Detect mobile device
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    
    // For mobile: Always open in new tab with save instructions
    if (isMobile) {
      const newTab = window.open('', '_blank');
      if (newTab) {
        newTab.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <meta name="viewport" content="width=device-width, initial-scale=1">
            <title>Guardar imagen</title>
            <style>
              * { margin: 0; padding: 0; box-sizing: border-box; }
              body { 
                font-family: -apple-system, BlinkMacSystemFont, sans-serif;
                background: #1e293b;
                min-height: 100vh;
                display: flex;
                flex-direction: column;
              }
              .header {
                background: #3b82f6;
                color: white;
                padding: 16px;
                text-align: center;
                position: sticky;
                top: 0;
                z-index: 10;
              }
              .header h3 { font-size: 16px; margin-bottom: 4px; }
              .header p { font-size: 13px; opacity: 0.9; }
              .image-container {
                flex: 1;
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 16px;
              }
              img { 
                max-width: 100%; 
                max-height: 80vh;
                border-radius: 8px;
                box-shadow: 0 4px 20px rgba(0,0,0,0.3);
              }
              .footer {
                padding: 16px;
                text-align: center;
              }
              .save-btn {
                display: inline-block;
                background: #22c55e;
                color: white;
                padding: 14px 28px;
                border-radius: 8px;
                text-decoration: none;
                font-weight: 600;
                font-size: 16px;
              }
            </style>
          </head>
          <body>
            <div class="header">
              <h3>${isIOS ? '📱 Mantén presionada la imagen' : '📱 Mantén presionada la imagen'}</h3>
              <p>${isIOS ? 'Selecciona "Guardar en Fotos"' : 'Selecciona "Descargar imagen"'}</p>
            </div>
            <div class="image-container">
              <img src="${imageData}" alt="TexVision - ${filename}" />
            </div>
            <div class="footer">
              <a href="${imageData}" download="${filename}" class="save-btn">
                Intentar descarga directa
              </a>
            </div>
          </body>
          </html>
        `);
        newTab.document.close();
        toast.success('Mantén presionada la imagen para guardarla');
      } else {
        toast.error('Permite ventanas emergentes en tu navegador');
      }
      return;
    }
    
    // Desktop: Try automatic download
    try {
      let blob;
      
      if (imageData.startsWith('data:')) {
        const parts = imageData.split(',');
        const mime = parts[0].match(/:(.*?);/)?.[1] || 'image/png';
        const base64Data = parts[1];
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        blob = new Blob([byteArray], { type: mime });
      } else {
        const response = await fetch(imageData);
        blob = await response.blob();
      }
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.cssText = 'position:fixed;left:-9999px;';
      
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.success(isUSA ? 'Image downloaded' : 'Imagen descargada');
      
    } catch (error) {
      console.error('Download error:', error);
      window.open(imageData, '_blank');
      toast.info(isUSA ? 'Right-click to save the image' : 'Haz clic derecho para guardar la imagen');
    }
  };

  if (loadingUsage) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="spinner" />
      </div>
    );
  }

  // Show payment verification overlay
  if (checkingPayment) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-4">
        <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center animate-pulse">
          <Sparkles className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-xl font-semibold text-slate-800">
          {isUSA ? 'Verifying your payment...' : 'Verificando tu pago...'}
        </h2>
        <p className="text-slate-500">
          {isUSA ? 'Please wait while we confirm your purchase' : 'Por favor espera mientras confirmamos tu compra'}
        </p>
        <div className="spinner mt-4" />
      </div>
    );
  }

  return (
    <div data-testid="texvision-page">
      <div className="page-header">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="page-title">TexHub AI</h1>
              <p className="page-subtitle">{isUSA ? 'AI-powered tools for fashion & textiles' : 'Herramientas de IA para moda y textiles'}</p>
            </div>
          </div>
          
          {/* Credits Badge */}
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-sm font-medium text-slate-700">
                <span className="flex items-center gap-1">
                  <Sparkles className="w-4 h-4 text-amber-500" />
                  <span className="text-amber-600 font-bold">{credits.remaining}</span>
                  <span className="text-slate-500">/ {credits.total_available} {isUSA ? 'credits' : 'créditos'}</span>
                </span>
              </p>
              <p className="text-xs text-slate-500">
                {credits.purchased_credits > 0 
                  ? `(${credits.monthly_credits} ${isUSA ? 'monthly' : 'mensuales'} + ${credits.purchased_credits} ${isUSA ? 'purchased' : 'comprados'})`
                  : `${credits.monthly_credits} ${isUSA ? 'monthly credits' : 'créditos mensuales'}`
                }
              </p>
            </div>
            <div className="w-24">
              <Progress value={credits.total_available > 0 ? (credits.remaining / credits.total_available) * 100 : 0} className="h-2" />
            </div>
            <Button 
              size="sm" 
              variant="outline"
              onClick={openCreditModal}
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
            >
              <Sparkles className="w-4 h-4 mr-1" />
              {isUSA ? 'Buy Credits' : 'Comprar'}
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs: Fashion Designer / Visual Search / Photo Optimizer / Trends / Gallery */}
      <div className="flex gap-2 mb-6 flex-wrap" data-testid="texvision-tabs">
        <Button
          variant={activeTab === 'designer' ? 'default' : 'outline'}
          onClick={() => setActiveTab('designer')}
          className={activeTab === 'designer' ? 'bg-purple-600 hover:bg-purple-700' : ''}
        >
          <Shirt className="w-4 h-4 mr-2" />
          {isUSA ? 'Fashion Designer' : 'Diseñador de Modas'}
        </Button>
        <Button
          variant={activeTab === 'visual-search' ? 'default' : 'outline'}
          onClick={() => setActiveTab('visual-search')}
          className={activeTab === 'visual-search' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
        >
          <Search className="w-4 h-4 mr-2" />
          {isUSA ? 'Visual Search' : 'Buscador Visual'}
        </Button>
        <Button
          variant={activeTab === 'photo-optimizer' ? 'default' : 'outline'}
          onClick={() => setActiveTab('photo-optimizer')}
          className={activeTab === 'photo-optimizer' ? 'bg-amber-600 hover:bg-amber-700' : ''}
        >
          <Wand2 className="w-4 h-4 mr-2" />
          {isUSA ? 'Photo Optimizer' : 'Optimizador de Fotos'}
        </Button>
        <Button
          variant={activeTab === 'trends' ? 'default' : 'outline'}
          onClick={() => setActiveTab('trends')}
          className={activeTab === 'trends' ? 'bg-indigo-600 hover:bg-indigo-700' : ''}
        >
          <TrendingUp className="w-4 h-4 mr-2" />
          TEXTREND AI
        </Button>
        <Button
          variant={activeTab === 'gallery' ? 'default' : 'outline'}
          onClick={() => setActiveTab('gallery')}
          className={activeTab === 'gallery' ? 'bg-purple-600 hover:bg-purple-700' : ''}
        >
          <FolderOpen className="w-4 h-4 mr-2" />
          {isUSA ? 'My images' : 'Mis imágenes'}
        </Button>
      </div>

      {/* TEXTREND AI Tab */}
      {activeTab === 'trends' && (
        <TexTrendAI 
          onCreditError={handleCreditError} 
          onSendToTexChat={(data) => {
            setTexChatContext({ type: 'textrend', data, image: null });
            setShowTexChat(true);
          }}
        />
      )}

      {/* Visual Search Tab */}
      {activeTab === 'visual-search' && (
        <VisualSearch 
          onSendToOptimizer={handleSendToOptimizer} 
          initialImage={visualSearchImage}
          onImageProcessed={() => setVisualSearchImage(null)}
          onCreditError={handleCreditError}
          onSendToTexChat={(data, image) => {
            setTexChatContext({ type: 'visual_search', data, image });
            setShowTexChat(true);
          }}
        />
      )}

      {/* Photo Optimizer Tab */}
      {activeTab === 'photo-optimizer' && (
        <PhotoOptimizer 
          initialImage={optimizerImage} 
          onImageProcessed={() => setOptimizerImage(null)}
          onSendToDesigner={handleSendToDesigner}
          onSendToDesignerAsFabric={handleSendToDesignerAsFabric}
          onSendToVisualSearch={handleSendToVisualSearch}
          onCreditError={handleCreditError}
        />
      )}

      {/* Fashion Designer Tab */}
      {activeTab === 'designer' && (
        <FashionDesigner 
          onSendToOptimizer={handleSendToOptimizer}
          initialImage={designerImage}
          onImageProcessed={() => setDesignerImage(null)}
          onCreditError={handleCreditError}
          onSendToTexChat={(data, image) => {
            setTexChatContext({ type: 'fashion_design', data, image });
            setShowTexChat(true);
          }}
        />
      )}

      {/* Gallery Tab */}
      {activeTab === 'gallery' && (
        <div data-testid="texvision-gallery">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FolderOpen className="w-5 h-5 text-purple-600" />
                {isUSA ? 'My generated images' : 'Mis imágenes generadas'}
              </CardTitle>
              <CardDescription>
                {isUSA ? 'All images you have created with TexHub AI' : 'Todas las imágenes que has creado con TexHub AI'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingHistory ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
                </div>
              ) : history.length === 0 ? (
                <div className="text-center py-12">
                  <Image className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500 mb-4">{isUSA ? "You haven't generated any images yet" : 'Aún no has generado ninguna imagen'}</p>
                  <Button onClick={() => setActiveTab('designer')} className="bg-purple-600 hover:bg-purple-700">
                    <Sparkles className="w-4 h-4 mr-2" />
                    {isUSA ? 'Create my first design' : 'Crear mi primer diseño'}
                  </Button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {history.map((item) => (
                      <div 
                        key={item.generation_id}
                        className="group relative aspect-square bg-slate-100 rounded-lg overflow-hidden border-2 border-slate-200 hover:border-purple-400 cursor-pointer transition-all"
                        onClick={() => setSelectedHistoryImage(item)}
                      >
                        {item.image_data ? (
                          <img 
                            src={item.image_data} 
                            alt={`${item.garment_type} - ${item.fabric_description}`}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Image className="w-12 h-12 text-slate-300" />
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center p-2">
                          <p className="text-white text-sm font-medium text-center">
                            {GARMENT_OPTIONS.find(g => g.id === item.garment_type)?.name || item.garment_type}
                          </p>
                          <p className="text-white/70 text-xs text-center mt-1 line-clamp-2">
                            {item.fabric_description}
                          </p>
                        </div>
                        <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button 
                            size="icon" 
                            variant="secondary" 
                            className="w-8 h-8"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (item.image_data) handleDownload(item.image_data, 0);
                            }}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-slate-500 mt-4 text-center">
                    {isUSA 
                      ? `${history.length} generated image${history.length !== 1 ? 's' : ''}`
                      : `${history.length} imagen${history.length !== 1 ? 'es' : ''} generada${history.length !== 1 ? 's' : ''}`
                    }
                  </p>
                </>
              )}
            </CardContent>
          </Card>

          {/* Image Preview Modal */}
          {selectedHistoryImage && (
            <div 
              className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedHistoryImage(null)}
            >
              <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
                <Button 
                  variant="ghost" 
                  size="icon"
                  className="absolute -top-12 right-0 text-white hover:bg-white/20"
                  onClick={() => setSelectedHistoryImage(null)}
                >
                  <X className="w-6 h-6" />
                </Button>
                <div className="bg-white rounded-xl overflow-hidden">
                  {selectedHistoryImage.image_data && (
                    <img 
                      src={selectedHistoryImage.image_data} 
                      alt="Preview"
                      className="w-full max-h-[70vh] object-contain"
                    />
                  )}
                  <div className="p-4 border-t">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-900">
                          {GARMENT_OPTIONS.find(g => g.id === selectedHistoryImage.garment_type)?.name || selectedHistoryImage.garment_type}
                        </p>
                        <p className="text-sm text-slate-500">{selectedHistoryImage.fabric_description}</p>
                        <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(selectedHistoryImage.created_at).toLocaleDateString('es-MX', { 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                      <Button 
                        onClick={() => {
                          if (selectedHistoryImage.image_data) {
                            handleDownload(selectedHistoryImage.image_data, 0);
                          }
                        }}
                        className="bg-purple-600 hover:bg-purple-700"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        {isUSA ? 'Download' : 'Descargar'}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pro Paywall */}
      <ProPaywall 
        isOpen={showPaywall} 
        onClose={() => setShowPaywall(false)} 
        feature="TexVision AI"
        reason="texvision"
      />

      {/* Credit Purchase Modal */}
      {showCreditModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">
                      {isUSA ? 'Buy AI Credits' : 'Comprar Créditos IA'}
                    </h2>
                    <p className="text-sm text-slate-500">
                      {isUSA ? 'Credits never expire' : 'Los créditos no expiran'}
                    </p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowCreditModal(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Current Balance */}
            <div className="px-6 py-4 bg-amber-50 border-b border-amber-100">
              <div className="flex items-center justify-between">
                <span className="text-amber-800">
                  {isUSA ? 'Your current balance:' : 'Tu balance actual:'}
                </span>
                <span className="text-2xl font-bold text-amber-600">
                  {credits.remaining} {isUSA ? 'credits' : 'créditos'}
                </span>
              </div>
            </div>

            {/* Packages */}
            <div className="p-6">
              <h3 className="text-sm font-medium text-slate-500 mb-4">
                {isUSA ? 'SELECT A PACKAGE' : 'SELECCIONA UN PAQUETE'}
              </h3>
              
              {loadingPackages ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
                </div>
              ) : (
                <div className="grid sm:grid-cols-3 gap-4">
                  {creditPackages.map((pkg) => (
                    <div 
                      key={pkg.package_id}
                      className={`relative rounded-xl border-2 p-5 transition-all hover:shadow-lg cursor-pointer ${
                        pkg.popular 
                          ? 'border-amber-400 bg-gradient-to-b from-amber-50 to-orange-50' 
                          : 'border-slate-200 hover:border-amber-300'
                      }`}
                      onClick={() => !purchasingPackage && purchaseCredits(pkg.package_id)}
                    >
                      {pkg.popular && (
                        <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-white border-0">
                          {isUSA ? 'Best Value' : 'Mejor Valor'}
                        </Badge>
                      )}
                      <div className="text-center">
                        <p className="text-4xl font-bold text-amber-600 mb-1">{pkg.credits}</p>
                        <p className="text-slate-500 text-sm mb-4">{isUSA ? 'credits' : 'créditos'}</p>
                        <p className="text-xl font-bold text-slate-900 mb-1">
                          {isUSA ? `$${pkg.price_usd} USD` : `$${pkg.price_mxn} MXN`}
                        </p>
                        <p className="text-xs text-slate-400">
                          ${(isUSA ? pkg.price_usd / pkg.credits : pkg.price_mxn / pkg.credits).toFixed(2)} / {isUSA ? 'credit' : 'crédito'}
                        </p>
                        <Button 
                          className={`w-full mt-4 ${pkg.popular ? 'bg-amber-500 hover:bg-amber-600' : 'bg-slate-800 hover:bg-slate-900'}`}
                          disabled={purchasingPackage === pkg.package_id}
                        >
                          {purchasingPackage === pkg.package_id ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : null}
                          {isUSA ? 'Buy Now' : 'Comprar'}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Credit Costs Reference */}
              <div className="mt-8 pt-6 border-t border-slate-200">
                <h4 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-500" />
                  {isUSA ? 'Credit costs per tool:' : 'Costo de créditos por herramienta:'}
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
                  <div className="flex justify-between bg-slate-50 rounded-lg px-3 py-2">
                    <span className="text-slate-600">{isUSA ? 'Fashion Designer' : 'Diseñador'}</span>
                    <span className="font-medium text-amber-600">5</span>
                  </div>
                  <div className="flex justify-between bg-slate-50 rounded-lg px-3 py-2">
                    <span className="text-slate-600">{isUSA ? 'Edit Design' : 'Editar Diseño'}</span>
                    <span className="font-medium text-amber-600">3</span>
                  </div>
                  <div className="flex justify-between bg-slate-50 rounded-lg px-3 py-2">
                    <span className="text-slate-600">{isUSA ? 'Apply Fabric' : 'Aplicar Tela'}</span>
                    <span className="font-medium text-amber-600">4</span>
                  </div>
                  <div className="flex justify-between bg-slate-50 rounded-lg px-3 py-2">
                    <span className="text-slate-600">{isUSA ? 'Visual Search' : 'Buscador Visual'}</span>
                    <span className="font-medium text-amber-600">2</span>
                  </div>
                  <div className="flex justify-between bg-slate-50 rounded-lg px-3 py-2">
                    <span className="text-slate-600">{isUSA ? 'Photo Optimizer' : 'Optimizador'}</span>
                    <span className="font-medium text-amber-600">3</span>
                  </div>
                  <div className="flex justify-between bg-slate-50 rounded-lg px-3 py-2">
                    <span className="text-slate-600">TexTrend AI</span>
                    <span className="font-medium text-amber-600">4</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* No Credits Modal - Shows when user runs out of credits */}
      {showNoCreditsModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-2xl">
            {/* Header with gradient */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-6 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-1">
                {isUSA ? 'Out of Credits!' : '¡Sin Créditos!'}
              </h2>
              <p className="text-amber-100">
                {isUSA 
                  ? `You need ${neededCredits || 'more'} credits to continue`
                  : `Necesitas ${neededCredits || 'más'} créditos para continuar`}
              </p>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Current balance */}
              <div className="bg-slate-50 rounded-xl p-4 mb-6 text-center">
                <p className="text-slate-500 text-sm">{isUSA ? 'Current balance' : 'Balance actual'}</p>
                <p className="text-3xl font-bold text-red-500">{credits.remaining}</p>
                <p className="text-slate-400 text-xs">{isUSA ? 'credits remaining' : 'créditos restantes'}</p>
              </div>

              {/* Options */}
              <div className="space-y-3">
                {/* Buy Credits - Primary */}
                <Button 
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold py-6"
                  onClick={() => {
                    setShowNoCreditsModal(false);
                    setShowCreditModal(true);
                    fetchCreditPackages();
                  }}
                >
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  {isUSA ? 'Buy Credits' : 'Comprar Créditos'}
                </Button>

                {/* Upgrade Plan - Secondary */}
                {user?.subscription_plan !== 'enterprise' && (
                  <Button 
                    variant="outline"
                    className="w-full border-2 border-purple-200 text-purple-700 hover:bg-purple-50 font-semibold py-6"
                    onClick={() => {
                      setShowNoCreditsModal(false);
                      navigate('/dashboard/subscription');
                    }}
                  >
                    <Crown className="w-5 h-5 mr-2" />
                    {user?.subscription_plan === 'pro' 
                      ? (isUSA ? 'Upgrade to Enterprise (500 credits/mo)' : 'Upgrade a Empresarial (500 créditos/mes)')
                      : (isUSA ? 'Upgrade to PRO (100 credits/mo)' : 'Upgrade a PRO (100 créditos/mes)')}
                  </Button>
                )}

                {/* Close */}
                <Button 
                  variant="ghost"
                  className="w-full text-slate-500"
                  onClick={() => setShowNoCreditsModal(false)}
                >
                  {isUSA ? 'Maybe later' : 'Quizás después'}
                </Button>
              </div>

              {/* Credit packs preview */}
              <div className="mt-6 pt-4 border-t border-slate-200">
                <p className="text-xs text-slate-400 text-center mb-3">
                  {isUSA ? 'Quick credit packs:' : 'Paquetes rápidos:'}
                </p>
                <div className="flex justify-center gap-4 text-center">
                  <div>
                    <p className="font-bold text-amber-600">50</p>
                    <p className="text-xs text-slate-400">${isUSA ? '5.99' : '99'}</p>
                  </div>
                  <div className="border-l border-slate-200 pl-4">
                    <p className="font-bold text-amber-600">150</p>
                    <p className="text-xs text-slate-400">${isUSA ? '14.99' : '249'}</p>
                  </div>
                  <div className="border-l border-slate-200 pl-4">
                    <p className="font-bold text-amber-600">400</p>
                    <p className="text-xs text-slate-400">${isUSA ? '32.99' : '549'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Floating TexChat Button */}
      <button
        onClick={() => {
          setTexChatContext({ type: null, data: null, image: null });
          setShowTexChat(true);
        }}
        className="fixed bottom-24 sm:bottom-6 right-4 sm:right-6 z-50 w-14 h-14 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-105 flex items-center justify-center group"
        data-testid="texchat-floating-button"
      >
        <MessageCircle className="w-6 h-6 text-white" />
        <span className="absolute -top-10 right-0 bg-slate-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap hidden sm:block">
          TexChat AI
        </span>
      </button>

      {/* TexChat Floating Panel */}
      <TexChatFloating
        isOpen={showTexChat}
        onClose={() => {
          setShowTexChat(false);
          setTexChatContext({ type: null, data: null, image: null });
        }}
        contextType={texChatContext.type}
        contextData={texChatContext.data}
        contextImage={texChatContext.image}
        onClearContext={() => setTexChatContext({ type: null, data: null, image: null })}
      />
    </div>
  );
};

export default TexVisionPage;
